import { FEATURES_CONFIG } from "../featuresConfig";

/**
 * The money trace — one console line for every action that moves a number the
 * player can read.
 *
 * ── Why a middleware, and not log lines at the call sites ──
 *
 * The balance, the free-rounds total and the free-spins total are each written
 * from several places — reducers, thunks, the win display, the outro, the
 * reload — and the bugs in this area have all been the same shape: a number
 * changed and nobody could say WHO changed it. A log at each writer answers
 * that only for the writers someone remembered to instrument.
 *
 * Here every plain action passes through one place, so the state is compared
 * before and after it and anything that moved is printed with the action that
 * moved it. A new writer is traced the day it is added, with no line of its own.
 *
 *   [$] freeRounds/incrementFreeRoundsDisplayedWin — fr.displayed 5 → 17.5
 *       | base.win=12.5 fs.win=3 fr=4/10
 *
 * The tail after `|` is CONTEXT, printed whether or not it changed: the two
 * rounds a win can be read from and the campaign's position. That is what
 * turns "the total went up by 12.5" into "the total went up by the BASE
 * round's win while a free spin paid 3".
 *
 * ── Cost ──
 *
 * With MONEY_DIAGNOSTICS off: one property read per action. With it on: two
 * getState calls and a couple of dozen field reads per action — nothing next
 * to a render. Thunks (functions) never reach here; redux-thunk sits in front
 * and dispatches their plain actions through, which is what gets traced.
 */

/** [label, read(state)] — the numbers the player reads, and what gates them. */
const FIELDS = [
  // Wallet
  ["balance", (s) => s.game?.balance],
  ["displayedBalance", (s) => s.game?.displayedBalance],
  ["replay.balance", (s) => s.replay?.replayBalance],
  ["replay.on", (s) => s.replay?.sessionActive],

  // The two rounds a win can come from
  ["base.win", (s) => s.game?.currentRound?.win],
  ["fs.win", (s) => s.freeSpins?.currentRound?.win],

  // Free spins
  ["fs.active", (s) => s.freeSpins?.active],
  ["fs.triggered", (s) => s.freeSpins?.triggered],
  ["fs.triggerWin", (s) => s.freeSpins?.triggerWin],
  ["fs.totalWin", (s) => s.freeSpins?.totalWin],
  ["fs.displayed", (s) => s.freeSpins?.displayedTotalWin],
  ["fs.capped", (s) => s.freeSpins?.winLimitReached],

  // Free rounds (the casino campaign)
  ["fr.status", (s) => s.freeRounds?.status],
  ["fr.win", (s) => s.freeRounds?.win],
  ["fr.displayed", (s) => s.freeRounds?.displayedWin],
  ["fr.synced", (s) => s.freeRounds?.syncedWin],
  ["fr.counted", (s) => s.freeRounds?.winCounted],
  ["fr.completionPending", (s) => s.freeRounds?.completionPending],
  ["fr.ended", (s) => s.freeRounds?.freeRoundsEnded],

  // What paces the win display
  ["anim.isAnimatedWin", (s) => s.animations?.isAnimatedWin],
  ["anim.winPlaying", (s) => s.animations?.winAnimationsPlaying],
  ["anim.counterStarted", (s) => s.animations?.counterAnimationStarted],
  ["anim.counterFinished", (s) => s.animations?.counterAnimationFinished],
];

const fmt = (value) => {
  if (value === undefined) return "-";
  if (value === null) return "null";
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : String(+value.toFixed(2));
  }
  return String(value);
};

const context = (s) => {
  const fr = s.freeRounds;
  const frPos = fr?.status ? ` fr=${fr.performed ?? 0}/${fr.count ?? 0}` : "";
  return `base.win=${fmt(s.game?.currentRound?.win)} fs.win=${fmt(
    s.freeSpins?.currentRound?.win,
  )}${frPos}`;
};

// ── Actions dispatched from INSIDE another action ──
//
// A store subscriber can dispatch while the outer action is still in `next()`
// — closing the free-rounds popup does exactly this with clearFreeRounds. The
// outer line then saw the inner action's changes as its own, and the same
// transition printed twice under two names. So each frame records the state at
// the moment its first nested dispatch begins: that is where ITS OWN reducer
// finished, and its line is diffed against that, not against the end.
const frames = [];

export const moneyTraceMiddleware = (store) => (next) => (action) => {
  if (!FEATURES_CONFIG.DEV_TOOLS?.MONEY_DIAGNOSTICS) return next(action);

  const parent = frames[frames.length - 1];
  if (parent && parent.own === undefined) parent.own = store.getState();

  const frame = { own: undefined };
  frames.push(frame);
  const before = store.getState();
  let result;
  try {
    result = next(action);
  } finally {
    frames.pop();
  }
  const after = frame.own ?? store.getState();

  const changes = [];
  for (const [label, read] of FIELDS) {
    const was = read(before);
    const now = read(after);
    if (was !== now) changes.push(`${label} ${fmt(was)} → ${fmt(now)}`);
  }
  if (changes.length) {
    // ONE string, never an object — the same rule as utils/fsDiag: a line you
    // can scan beats a fold-out you have to click.
    // eslint-disable-next-line no-console
    console.log(`[$] ${action?.type} — ${changes.join(" · ")} | ${context(after)}`);
  }
  return result;
};

/**
 * One line from outside Redux, in the same channel — for derived state that is
 * never stored, like which row the win display is showing.
 */
export const moneyDiag = (tag, text) => {
  if (!FEATURES_CONFIG.DEV_TOOLS?.MONEY_DIAGNOSTICS) return;
  // eslint-disable-next-line no-console
  console.log(`[$] ${tag} — ${text}`);
};
